class ProfileSurvey {
  constructor(config) {
    this.surveyPath = config.surveyPath;
    this.skipPath = config.skipPath;
    this.surveyQuestions = {
      primary_role: {
        text: "Which best describes your role?",
        type: "single_choice",
        options: ["SRE / DevOps", "IT Manager", "Engineering Lead", "CTO", "Other"]
      },
      other_roles: {
        text: "Who else in your company will need outage alerts?",
        type: "multi_choice",
        options: ["Engineering", "IT Ops", "Customer Support", "Product", "Leadership"]
      },
      primary_goal: {
        text: "What's the main job you're hiring IsDown to do?",
        type: "single_choice",
        options: [
          "Know first when vendors go down",
          "Share vendor status on our status page",
          "Track vendor reliability over time",
          "Other"
        ]
      },
      // main_objection: {
      //   text: "Biggest hesitation before paying for IsDown?",
      //   type: "single_choice",
      //   options: [
      //     "Price",
      //     "Not sure we'd use it enough",
      //     "We might build it ourselves",
      //     "It's not a priority right now",
      //     "Other"
      //   ]
      // },
      how_did_you_find_us: {
        text: "How did you find us?",
        type: "single_choice",
        options: [
          "Google / Bing Search",
          "Social Media",
          "Recommendation from colleague",
          "Blog",
          "ChatGPT / Claude / Other LLM",
          "Other"
        ]
      },
      first_name_profile: {
        text: "Tell us your first name so we can say hi properly",
        type: "text",
        optional: true
      }
    };

    this.questionOrder = ['primary_role', 'other_roles', 'primary_goal', 'how_did_you_find_us', /* 'main_objection', */ 'first_name_profile'];
    this.currentQuestionIndex = 0;
    this.surveyAnswers = {};

    // Bind methods to this context
    this.initialize = this.initialize.bind(this);
    this.showModal = this.showModal.bind(this);
    this.hideModal = this.hideModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.displayCurrentQuestion = this.displayCurrentQuestion.bind(this);
    this.submitAnswer = this.submitAnswer.bind(this);
    this.submitMultiChoice = this.submitMultiChoice.bind(this);
    this.submitText = this.submitText.bind(this);
    this.skipQuestion = this.skipQuestion.bind(this);
    this.completeSurvey = this.completeSurvey.bind(this);
    this.showOtherInput = this.showOtherInput.bind(this);
    this.submitOtherAnswer = this.submitOtherAnswer.bind(this);
    this.showError = this.showError.bind(this);
    this.clearError = this.clearError.bind(this);
  }

  initialize() {
    // Check if user manually closed the modal before
    if (localStorage.getItem('profileSurveyClosed') === 'true') {
      return; // Don't show if user explicitly closed it
    }

    // Load saved progress from localStorage
    const savedProgress = localStorage.getItem('profileSurveyProgress');
    if (savedProgress) {
      this.surveyAnswers = JSON.parse(savedProgress);
      // Find the first unanswered question
      for (let i = 0; i < this.questionOrder.length; i++) {
        if (!this.surveyAnswers[this.questionOrder[i]] && !this.surveyAnswers[this.questionOrder[i] + '_skipped']) {
          this.currentQuestionIndex = i;
          break;
        }
      }
    }

    // Check if survey should be shown
    fetch(`${this.surveyPath}.json`)
      .then(response => response.json())
      .then(data => {
        // Check if survey is already completed
        if (data.survey_completed) {
          return;
        }

        // Load any existing answers
        if (data.answers) {
          Object.keys(data.answers).forEach(key => {
            if (!key.endsWith('_skipped') && data.answers[key]) {
              this.surveyAnswers[key] = data.answers[key];
            }
          });
        }

        // Check if we should show the modal
        const mandatoryQuestions = ['primary_role', 'other_roles', 'primary_goal', 'how_did_you_find_us' /*, 'main_objection' */];
        const mandatoryComplete = mandatoryQuestions.every(q =>
          this.surveyAnswers[q] || data.answers?.[`${q}_skipped`]
        );

        if (!mandatoryComplete) {
          this.showModal();
          this.displayCurrentQuestion();
        }
      });
  }

  showModal() {
    document.getElementById('profile-survey-modal').classList.remove('hidden');
    document.body.style.overflow = 'hidden'; // Prevent body scroll
  }

  hideModal() {
    document.getElementById('profile-survey-modal').classList.add('hidden');
    document.body.style.overflow = ''; // Restore body scroll
  }

  closeModal() {
    // Save current state to localStorage
    localStorage.setItem('profileSurveyClosed', 'true');
    this.hideModal();
  }

  displayCurrentQuestion() {
    if (this.currentQuestionIndex >= this.questionOrder.length) {
      this.completeSurvey();
      return;
    }

    const questionKey = this.questionOrder[this.currentQuestionIndex];
    const question = this.surveyQuestions[questionKey];
    const container = document.getElementById('survey-question-container');

    // Update progress bar and text
    const progress = ((this.currentQuestionIndex + 1) / this.questionOrder.length) * 100;
    document.getElementById('survey-progress-bar').style.width = progress + '%';
    document.getElementById('survey-progress-text').textContent = `Question ${this.currentQuestionIndex + 1} of ${this.questionOrder.length}`;

    let html = `
      <div class="animate-fade-in">
        <div id="survey-error-message" class="hidden mb-4 p-3 bg-red-900/20 border border-red-500/50 rounded-lg text-red-400 text-sm"></div>
        <h3 class="text-lg font-semibold text-white mb-4">
          ${question.text}
          ${question.optional ? '<span class="text-sm text-gray-400 ml-2">(optional)</span>' : ''}
        </h3>
    `;

    if (question.type === 'single_choice') {
      html += '<div class="space-y-2">';
      question.options.forEach(option => {
        if (option === "Other") {
          html += `
            <button
              data-action="show-other-input"
              data-question="${questionKey}"
              class="w-full text-left p-3 rounded-lg border border-gray-600 hover:border-blue-500 hover:bg-neutral-700 transition-all duration-200 text-gray-200 hover:text-white">
              ${option}
            </button>
          `;
        } else {
          html += `
            <button
              data-action="submit-answer"
              data-question="${questionKey}"
              data-answer="${option}"
              class="w-full text-left p-3 rounded-lg border border-gray-600 hover:border-blue-500 hover:bg-neutral-700 transition-all duration-200 text-gray-200 hover:text-white">
              ${option}
            </button>
          `;
        }
      });
      html += '</div>';

      // Add hidden input field for "Other" option
      html += `
        <div id="other-input-container-${questionKey}" class="hidden mt-4 space-y-3">
          <p class="text-sm text-gray-400">Please specify:</p>
          <input
            type="text"
            id="other-input-${questionKey}"
            placeholder="Type your answer..."
            class="w-full p-3 bg-neutral-700 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
            data-question="${questionKey}">
          <button
            data-action="submit-other"
            data-question="${questionKey}"
            class="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors">
            Continue →
          </button>
        </div>
      `;
    } else if (question.type === 'multi_choice') {
      html += '<div class="space-y-2 mb-4">';
      question.options.forEach(option => {
        html += `
          <label class="flex items-center p-3 rounded-lg border border-gray-600 hover:border-blue-500 hover:bg-neutral-700 transition-all duration-200 cursor-pointer">
            <input
              type="checkbox"
              name="survey_multi_choice"
              value="${option}"
              class="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500">
            <span class="ml-3 text-gray-200">${option}</span>
          </label>
        `;
      });
      html += `
        </div>
        <button
          data-action="submit-multi"
          data-question="${questionKey}"
          class="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors">
          Continue →
        </button>
      `;
    } else if (question.type === 'text') {
      html += `
        <div class="space-y-4">
          <input
            type="text"
            id="survey-text-input"
            placeholder="Type your answer..."
            class="w-full p-3 bg-neutral-700 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
            data-question="${questionKey}">
          <button
            data-action="submit-text"
            data-question="${questionKey}"
            class="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors">
            ${question.optional ? 'Continue →' : 'Submit →'}
          </button>
        </div>
      `;
    }

    html += '</div>';
    container.innerHTML = html;

    // Add event listeners to the new elements
    this.attachEventListeners();
  }

  attachEventListeners() {
    // Single choice buttons
    document.querySelectorAll('[data-action="submit-answer"]').forEach(button => {
      button.addEventListener('click', (e) => {
        const question = e.target.dataset.question;
        const answer = e.target.dataset.answer;
        this.submitAnswer(question, answer);
      });
    });

    // Show other input field buttons
    document.querySelectorAll('[data-action="show-other-input"]').forEach(button => {
      button.addEventListener('click', (e) => {
        const question = e.target.dataset.question;
        this.showOtherInput(question);
      });
    });

    // Submit other input buttons
    document.querySelectorAll('[data-action="submit-other"]').forEach(button => {
      button.addEventListener('click', (e) => {
        const question = e.target.dataset.question;
        this.submitOtherAnswer(question);
      });
    });

    // Other input enter key
    document.querySelectorAll('[id^="other-input-"]').forEach(input => {
      input.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          const question = e.target.dataset.question;
          this.submitOtherAnswer(question);
        }
      });
    });

    // Multi choice submit button
    const multiSubmit = document.querySelector('[data-action="submit-multi"]');
    if (multiSubmit) {
      multiSubmit.addEventListener('click', (e) => {
        const question = e.target.dataset.question;
        this.submitMultiChoice(question);
      });
    }

    // Text input submit button
    const textSubmit = document.querySelector('[data-action="submit-text"]');
    if (textSubmit) {
      textSubmit.addEventListener('click', (e) => {
        const question = e.target.dataset.question;
        this.submitText(question);
      });
    }

    // Text input enter key
    const textInput = document.getElementById('survey-text-input');
    if (textInput) {
      textInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          const question = e.target.dataset.question;
          this.submitText(question);
        }
      });
    }
  }

  submitAnswer(question, answer) {
    this.surveyAnswers[question] = answer;
    localStorage.setItem('profileSurveyProgress', JSON.stringify(this.surveyAnswers));

    fetch(this.surveyPath, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ question: question, answer: answer })
    })
    .then(response => response.json())
    .then(() => {
      this.currentQuestionIndex++;
      this.displayCurrentQuestion();
    });
  }

  submitMultiChoice(question) {
    const checkboxes = document.querySelectorAll('input[name="survey_multi_choice"]:checked');
    const answers = Array.from(checkboxes).map(cb => cb.value);

    if (answers.length === 0) {
      this.showError('Please select at least one option');
      return;
    }

    this.surveyAnswers[question] = answers;
    localStorage.setItem('profileSurveyProgress', JSON.stringify(this.surveyAnswers));

    fetch(this.surveyPath, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ question: question, answer: answers })
    })
    .then(response => response.json())
    .then(() => {
      this.currentQuestionIndex++;
      this.displayCurrentQuestion();
    });
  }

  submitText(question) {
    const input = document.getElementById('survey-text-input');
    const answer = input.value.trim();

    if (!answer && !this.surveyQuestions[question].optional) {
      return;
    }

    if (answer) {
      this.surveyAnswers[question] = answer;
      localStorage.setItem('profileSurveyProgress', JSON.stringify(this.surveyAnswers));

      fetch(this.surveyPath, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ question: question, answer: answer })
      })
      .then(response => response.json())
      .then(() => {
        this.currentQuestionIndex++;
        this.displayCurrentQuestion();
      });
    } else {
      this.skipQuestion();
    }
  }

  showOtherInput(question) {
    // Hide all option buttons
    const container = document.getElementById('survey-question-container');
    const buttonsDiv = container.querySelector('.space-y-2');
    if (buttonsDiv) {
      buttonsDiv.style.display = 'none';
    }

    // Show the other input field
    const otherContainer = document.getElementById(`other-input-container-${question}`);
    if (otherContainer) {
      otherContainer.classList.remove('hidden');
      // Focus on the input field
      const input = document.getElementById(`other-input-${question}`);
      if (input) {
        input.focus();
      }
    }
  }

  submitOtherAnswer(question) {
    const input = document.getElementById(`other-input-${question}`);
    const answer = input ? input.value.trim() : '';

    if (!answer) {
      this.showError('Please provide your answer');
      return;
    }

    // Store as "Other: [user input]"
    const formattedAnswer = `Other: ${answer}`;
    this.submitAnswer(question, formattedAnswer);
  }

  skipQuestion() {
    const question = this.questionOrder[this.currentQuestionIndex];

    fetch(this.skipPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ question: question })
    })
    .then(response => response.json())
    .then(() => {
      this.surveyAnswers[question + '_skipped'] = true;
      localStorage.setItem('profileSurveyProgress', JSON.stringify(this.surveyAnswers));
      this.currentQuestionIndex++;
      this.displayCurrentQuestion();
    });
  }

  completeSurvey() {
    localStorage.removeItem('profileSurveyProgress');
    localStorage.removeItem('profileSurveyClosed');
    this.hideModal();
    // Optionally refresh the page or show a success message
  }

  showError(message) {
    const errorElement = document.getElementById('survey-error-message');
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.remove('hidden');

      // Add a subtle shake animation
      errorElement.style.animation = 'shake 0.3s';
      setTimeout(() => {
        if (errorElement) {
          errorElement.style.animation = '';
        }
      }, 300);

      // Auto-hide after 5 seconds
      setTimeout(() => {
        this.clearError();
      }, 5000);
    }
  }

  clearError() {
    const errorElement = document.getElementById('survey-error-message');
    if (errorElement) {
      errorElement.classList.add('hidden');
      errorElement.textContent = '';
    }
  }
}

// Export for use in other files
window.ProfileSurvey = ProfileSurvey;